import { computed, ref } from 'vue'
import type { ComputedRef, Ref } from 'vue'

export interface SelectableItem {
  id: string
  bytes: number
}

export interface UseGallerySelection {
  selected: Ref<Set<string>>
  count: ComputedRef<number>
  selectedBytes: ComputedRef<number>
  isSelected: (id: string) => boolean
  toggle: (id: string) => void
  selectRange: (toId: string) => void
  selectAll: () => void
  clear: () => void
}

/** Selection set with shift-range select and select-all over the currently visible items. */
export function useGallerySelection(visibleItems: () => SelectableItem[]): UseGallerySelection {
  const selected = ref<Set<string>>(new Set())
  let anchorId: string | null = null

  const count = computed(() => selected.value.size)
  const selectedBytes = computed(() =>
    visibleItems().reduce((sum, item) => (selected.value.has(item.id) ? sum + item.bytes : sum), 0),
  )

  function isSelected(id: string): boolean {
    return selected.value.has(id)
  }

  function toggle(id: string): void {
    const next = new Set(selected.value)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    selected.value = next
    anchorId = id
  }

  function selectRange(toId: string): void {
    const ids = visibleItems().map((i) => i.id)
    const to = ids.indexOf(toId)
    const from = anchorId === null ? -1 : ids.indexOf(anchorId)
    if (to === -1 || from === -1) {
      toggle(toId)
      return
    }
    const [start, end] = from <= to ? [from, to] : [to, from]
    const next = new Set(selected.value)
    for (const id of ids.slice(start, end + 1)) next.add(id)
    selected.value = next
  }

  function selectAll(): void {
    selected.value = new Set(visibleItems().map((i) => i.id))
  }

  function clear(): void {
    selected.value = new Set()
    anchorId = null
  }

  return { selected, count, selectedBytes, isSelected, toggle, selectRange, selectAll, clear }
}
